/**
 * Compact match row for scoreboard: [Home name column] [Home kit] [Score/hour] [Away kit] [Away name column].
 * Status label (LIVE / FINAL) is absolutely positioned above the score so jerseys stay level with the score line.
 */

import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { SoccerKit } from '../../ui/SoccerKit';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { formatTimeOnly } from '../../../helpers/formatDateTime.helper';
import type { GameResult } from '../../../src/gameResults/types/gameResults.types';
import {
  GAME_STATUSES,
  SCOREBOARD_COMPLETED_LABEL,
} from '../../../src/gameResults/constants/gameResults.constants';
import { getScoreboardMatchRowStyles } from './ScoreboardMatchRow.styles';

export interface ScoreboardMatchRowProps {
  game: GameResult;
  /** When true, removes bottom border (last row in league card). */
  isLast?: boolean;
  testID?: string;
  /** When set, row is pressable and calls this handler (e.g. open Match Hub). */
  onPress?: (game: GameResult) => void;
}

export const ScoreboardMatchRow: React.FC<ScoreboardMatchRowProps> = ({
  game,
  isLast,
  testID,
  onPress,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getScoreboardMatchRowStyles(theme), [theme]);

  const homeKitConfig = useMemo(
    () => game.homeKit ?? getTeamKitConfig(game.homeTeam),
    [game.homeKit, game.homeTeam]
  );
  const awayKitConfig = useMemo(
    () => game.awayKit ?? getTeamKitConfig(game.awayTeam),
    [game.awayKit, game.awayTeam]
  );

  const isUpcoming = game.status === GAME_STATUSES.UPCOMING;
  const isLive = game.status === GAME_STATUSES.LIVE;
  const isCompleted = game.status === GAME_STATUSES.COMPLETED;

  const homeLost = isCompleted && game.homeScore < game.awayScore;
  const awayLost = isCompleted && game.awayScore < game.homeScore;

  const centerTime = formatTimeOnly(game.date);

  // National-team competitions send a flagcdn code per side; show the flag (kit as fallback)
  // instead of the club kit. Club leagues send no code, so the kit renders unchanged.
  const homeHasFlag = game.homeCountryCode != null && game.homeCountryCode !== '';
  const awayHasFlag = game.awayCountryCode != null && game.awayCountryCode !== '';

  const homeCrest = homeHasFlag ? (
    <CountryFlag
      countryCode={game.homeCountryCode as string}
      kitFallback={homeKitConfig}
      size="mini"
      accessibilityLabel={game.homeTeam}
    />
  ) : (
    <SoccerKit
      primary={homeKitConfig.primary}
      secondary={homeKitConfig.secondary}
      type={homeKitConfig.type}
      size={22}
    />
  );

  const awayCrest = awayHasFlag ? (
    <CountryFlag
      countryCode={game.awayCountryCode as string}
      kitFallback={awayKitConfig}
      size="mini"
      accessibilityLabel={game.awayTeam}
    />
  ) : (
    <SoccerKit
      primary={awayKitConfig.primary}
      secondary={awayKitConfig.secondary}
      type={awayKitConfig.type}
      size={22}
    />
  );

  const rowContent = (
    <View style={[styles.row, isLast && styles.rowLast]}>
      {/* Home team name */}
      <View style={styles.nameColumnLeft}>
        <Text
          style={[styles.teamName, styles.teamNameLeft, homeLost && styles.teamNameDim]}
          numberOfLines={2}
        >
          {game.homeTeam}
        </Text>
      </View>

      {/* Home crest — flag for national teams, kit for clubs */}
      <View style={styles.kitCell}>
        {homeCrest}
      </View>

      {/* Center block — score is the layout anchor; status label is absolutely positioned above */}
      <View style={styles.centerBlock}>
        {isLive && (
          <View style={styles.statusLabel}>
            <View style={styles.liveLabel}>
              <View style={styles.liveDot} />
              <Text style={styles.liveLabelText}>LIVE</Text>
            </View>
          </View>
        )}
        {isCompleted && (
          <View style={styles.statusLabel}>
            <Text style={styles.finalLabel}>{SCOREBOARD_COMPLETED_LABEL}</Text>
          </View>
        )}
        {isUpcoming && <Text style={styles.centerTime}>{centerTime}</Text>}
        {(isLive || isCompleted) && (
          <View style={styles.scoreRow}>
            <Text style={[styles.scoreDigit, homeLost && styles.scoreDigitDim]}>
              {game.homeScore}
            </Text>
            <Text style={styles.scoreSeparator}>-</Text>
            <Text style={[styles.scoreDigit, awayLost && styles.scoreDigitDim]}>
              {game.awayScore}
            </Text>
          </View>
        )}
      </View>

      {/* Away crest — flag for national teams, kit for clubs */}
      <View style={styles.kitCell}>
        {awayCrest}
      </View>

      {/* Away team name */}
      <View style={styles.nameColumnRight}>
        <Text
          style={[styles.teamName, awayLost && styles.teamNameDim]}
          numberOfLines={2}
        >
          {game.awayTeam}
        </Text>
      </View>
    </View>
  );

  if (onPress !== undefined) {
    return (
      <Pressable
        onPress={() => {
          onPress(game);
        }}
        style={({ pressed, hovered }) => [
          styles.pressableOuter,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.rowPressed,
          hovered && Platform.OS === 'web' && styles.pressableHover,
        ]}
        testID={testID ?? `scoreboard-match-row-${game.id}`}
      >
        {rowContent}
      </Pressable>
    );
  }

  return (
    <View testID={testID ?? `scoreboard-match-row-${game.id}`}>
      {rowContent}
    </View>
  );
};
